import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import LoginPage from './components/auth/LoginPage';
import PatientManager from './components/PatientManager';
import NavBar from './components/NavBar';
import UserManagement from './components/UserManagement';
import IVAnalyzer from './components/IVAnalyzer';
import HydraChatPage from './components/HydraChatPage';
import RequestCenter from './components/RequestCenter';
import APIKeyTest from './components/APIKeyTest';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ClientPage from './components/ClientPage';
import BusinessSettings from './components/Settings/BusinessSettings';
import NotificationSettings from './components/Settings/NotificationSettings';
import AdminPanel from './components/Admin/AdminPanel';
import DocumentBrowser from './components/DocumentBrowser';
import PublicBookingPage from './pages/PublicBookingPage';
import { ChatPage } from './components/Chat';

// Protected route component that checks if user is authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Admin route component that checks if user is an admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Determine if this is a booking page that shouldn't have padding
  const isBookingPage = location.pathname.startsWith('/booking');

  return (
    <>
      {isAuthenticated && <NavBar />}
      <Box sx={{ p: isBookingPage ? 0 : { xs: 1, sm: 2, md: 3 } }}>
        <Routes>
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/" /> : <LoginPage />
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <PatientManager />
            </ProtectedRoute>
          } />
          <Route path="/client/:id" element={
            <ProtectedRoute>
              <ClientPage />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          } />
          <Route path="/hydra-chat" element={
            <ProtectedRoute>
              <HydraChatPage />
            </ProtectedRoute>
          } />
          <Route path="/request-center" element={
            <ProtectedRoute>
              <RequestCenter />
            </ProtectedRoute>
          } />
          {/* Redirect old URLs to new RequestCenter */}
          <Route path="/forms" element={<Navigate to="/request-center" replace />} />
          <Route path="/job-status" element={<Navigate to="/request-center?tab=1" replace />} />
          <Route path="/api-key-test" element={
            <ProtectedRoute>
              <APIKeyTest />
            </ProtectedRoute>
          } />
          <Route path="/business-settings" element={
            <AdminRoute>
              <BusinessSettings />
            </AdminRoute>
          } />
          <Route path="/notification-settings" element={
            <AdminRoute>
              <NotificationSettings />
            </AdminRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />
          <Route path="/documents" element={<DocumentBrowser />} />
          <Route path="/documents-protected" element={
            <ProtectedRoute>
              <DocumentBrowser />
            </ProtectedRoute>
          } />
          
          <Route path="/chat" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          
          {/* Public Booking Routes - No Authentication Required */}
          <Route path="/booking" element={<PublicBookingPage />} />
          <Route path="/booking/:clinicPath" element={<PublicBookingPage />} />
          
          {/* Admin Routes for various tabs */}
          <Route path="/admin/notifications" element={
            <AdminRoute>
              <AdminPanel activeTab="notifications" />
            </AdminRoute>
          } />
          
          <Route path="/admin/clinics" element={
            <AdminRoute>
              <AdminPanel activeTab="clinics" />
            </AdminRoute>
          } />
          
          <Route path="/admin/logs" element={
            <AdminRoute>
              <AdminPanel activeTab="logs" />
            </AdminRoute>
          } />
          
          <Route path="/admin/prices" element={
            <AdminRoute>
              <AdminPanel activeTab="prices" />
            </AdminRoute>
          } />
          
          <Route path="/admin/practitioners" element={
            <AdminRoute>
              <AdminPanel activeTab="practitioners" />
            </AdminRoute>
          } />
          
          {/* Check if path starts with '/booking' to allow booking page completion */}
          <Route path="/booking/*" element={<PublicBookingPage />} />
          
          {/* 404 Page for public routes, redirect for authenticated routes */}
          <Route path="*" element={
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h4" gutterBottom>Page Not Found</Typography>
              <Typography variant="body1">The page you're looking for doesn't exist.</Typography>
              <Button 
                variant="contained" 
                component={Link} 
                to="/booking/bettermd" 
                sx={{ mt: 4 }}
              >
                Go to Booking Page
              </Button>
            </Box>
          } />
        </Routes>
      </Box>
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <CssBaseline />
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
